import type { ElegantConstRoute } from '@elegant-router/types';
import type { HiddenRouteEnv } from './hidden-routes.shared';
import {
  DEFAULT_ENVIRONMENTS,
  HIDDEN_ROUTES,
  activeHiddenRouteEntries,
  buildLookups,
  componentToViewPath,
  isInvoicePortalAvailable
} from './hidden-routes.shared';

/**
 * Hidden routes configuration (runtime wiring)
 * -------------------------------------------
 *
 * This module wires the environment-agnostic definitions in
 * `hidden-routes.shared.ts` into the running app. It lets us keep in-progress
 * views (for example `src/views/sponsorship` or `src/views/model-studio`) in the
 * codebase and use them freely during local development, while controlling
 * exactly WHEN they are registered in the router and rendered in the sidebar
 * (`src/layouts/modules/global-menu/base-menu.vue`).
 *
 * Because both the vue-router records and the global menu are derived from the
 * same route table produced by `createStaticRoutes()` in
 * `src/router/routes/index.ts`, filtering here hides the route everywhere at
 * once: no route record, no menu entry, no reachable URL.
 *
 * How matching works
 * ------------------
 * A route is hidden when it matches an active entry in `HIDDEN_ROUTES` by EITHER
 * its `routeNames` (elegant-router route name) or its `viewPathPrefixes`
 * (`src/views/...` folder). Matching a parent hides the whole branch.
 *
 * Environment mode targeting
 * -------------------------
 * Each entry declares which environments it should be hidden in via
 * `environments`:
 *
 * - `['prod']`          -> hidden in production builds only (default).
 * - `['local']`         -> hidden only during local dev on Vite.
 * - `['local', 'prod']` -> hidden EVERYWHERE: both `pnpm run dev` and prod build.
 *
 * The current environment is resolved from Vite's `import.meta.env.PROD`.
 *
 * Escape hatch
 * -----------
 * `VITE_SHOW_HIDDEN_ROUTES=Y` forces every hidden route to remain visible
 * (useful for staging/QA), regardless of `environments`.
 */

export type { HiddenRouteEnv, HiddenRouteEntry } from './hidden-routes.shared';
export { HIDDEN_ROUTES } from './hidden-routes.shared';

/** The environment mode of the current build. */
const viteEnv = (import.meta as ImportMeta & { env?: Partial<ImportMetaEnv> }).env ?? {};

export const CURRENT_ENV: HiddenRouteEnv = viteEnv.PROD ? 'prod' : 'local';

/** Invoice-specific navigation release decision; backend capabilities still control actions. */
export const INVOICE_PORTAL_AVAILABLE = isInvoicePortalAvailable(
  CURRENT_ENV,
  viteEnv.VITE_INVOICE_PORTAL_ENABLED
);

/**
 * Escape hatch: force all hidden routes to remain visible even when configured
 * to hide. Useful for staging/QA. Set `VITE_SHOW_HIDDEN_ROUTES=Y` to enable.
 */
const FORCE_SHOW_HIDDEN = viteEnv.VITE_SHOW_HIDDEN_ROUTES === 'Y';

/**
 * Whether a single route should be hidden, based on its name and view path.
 * (Does not consider ancestry; the recursive filter handles branches.)
 */
function isRouteHidden(
  route: ElegantConstRoute,
  routeNames: Set<string>,
  viewPathPrefixes: string[]
): boolean {
  if (FORCE_SHOW_HIDDEN) return false;

  if (route.name && routeNames.has(route.name)) return true;

  const viewPath = componentToViewPath(route.component);
  if (viewPath && viewPathPrefixes.some(prefix => viewPath === prefix || viewPath.startsWith(`${prefix}/`))) {
    return true;
  }

  return false;
}

export interface HiddenRouteReport {
  /** Route names that were removed from the route table. */
  hiddenRouteNames: string[];
  /** Whether anything was hidden at all. */
  hasHidden: boolean;
}

/**
 * Recursively filter the route tree, removing any route (and its subtree) that
 * matches the hidden configuration for the current environment. Returns the
 * filtered tree plus a report of everything removed so callers can warn.
 */
export function filterHiddenRoutes(routes: ElegantConstRoute[]): {
  routes: ElegantConstRoute[];
  report: HiddenRouteReport;
} {
  const { routeNames, viewPathPrefixes } = buildLookups(HIDDEN_ROUTES, CURRENT_ENV, INVOICE_PORTAL_AVAILABLE);
  const hiddenRouteNames: string[] = [];

  if (FORCE_SHOW_HIDDEN || (routeNames.size === 0 && viewPathPrefixes.length === 0)) {
    return { routes, report: { hiddenRouteNames: [], hasHidden: false } };
  }

  function collectNames(route: ElegantConstRoute) {
    if (route.name) hiddenRouteNames.push(route.name);
    route.children?.forEach(collectNames);
  }

  function walk(items: ElegantConstRoute[]): ElegantConstRoute[] {
    return items.reduce<ElegantConstRoute[]>((acc, route) => {
      if (isRouteHidden(route, routeNames, viewPathPrefixes)) {
        collectNames(route);
        return acc;
      }

      if (route.children?.length) {
        const children = walk(route.children);
        acc.push({ ...route, children });
        return acc;
      }

      acc.push(route);
      return acc;
    }, []);
  }

  const filtered = walk(routes);

  return {
    routes: filtered,
    report: {
      hiddenRouteNames,
      hasHidden: hiddenRouteNames.length > 0
    }
  };
}

let hasWarned = false;

/**
 * Emit a single, clearly-formatted warning listing every route that has been
 * hidden for the current environment. Called once when the static routes are
 * constructed so developers immediately notice which features are gated — both
 * during `pnpm run dev` and at runtime after a `pnpm run build`.
 */
export function warnHiddenRoutes(report: HiddenRouteReport): void {
  if (!report.hasHidden || hasWarned) return;
  hasWarned = true;

  const envLabel = CURRENT_ENV === 'prod' ? 'production build' : 'local dev build';
  const list = report.hiddenRouteNames.map(name => `  • ${name}`).join('\n');
  const reasons = activeHiddenRouteEntries(HIDDEN_ROUTES, CURRENT_ENV, INVOICE_PORTAL_AVAILABLE)
    .map(entry => {
      const target = [...(entry.routeNames ?? []), ...(entry.viewPathPrefixes ?? [])].join(', ');
      const envs = (entry.environments ?? DEFAULT_ENVIRONMENTS).join(', ');
      return `  • ${target} [${envs}]${entry.reason ? ` — ${entry.reason}` : ''}`;
    })
    .join('\n');

  // eslint-disable-next-line no-console
  console.warn(
    [
      '',
      `⚠️  [hidden-routes] The following routes are HIDDEN in this ${envLabel} (env="${CURRENT_ENV}"):`,
      list,
      '',
      'Configured in src/router/routes/hidden-routes.shared.ts:',
      reasons,
      '',
      'To temporarily reveal them (e.g. staging/QA), build with VITE_SHOW_HIDDEN_ROUTES=Y.',
      ''
    ].join('\n')
  );
}
